import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { getAllPossibleExpressions, getTranslations } from "store/selectors/translations";
import { useTranslation } from "react-i18next"; 
import { setIsTranslateModelEnabledBS } from "utils/builder/IScheduleLogic";
import { translateExpressions } from "store/actions/translations";
import { useDispatch } from "react-redux";

const DashboardStateContext = createContext();

const DashboardStateProvider = ({ children }) => {
  const { i18n } = useTranslation()
  const dispatch = useDispatch()

  const [foldersTree, setFoldersTree] = useState(null);
  const [activeFolder, setActiveFolder] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [isFolderLoading, setIsFolderLoading] = useState(false);
  const [favourites, setFavourites] = useState(null);
  const [recent, setRecent] = useState(null);
  const [isTableHidden, setIsTableHidden] = useState(true);
  const [checkSidebarDropdownStatus, setCheckSidebarDropdownStatus] = useState(true);
  const [isModalLoaded, setIsModalLoaded] = useState({ status: false });
  const [isWelcomeModalSeen, setIsWelcomeModalSeen] = useState(false);
  const [signingState, setSigningState] = useState(null);
  const isGlobalTranslationOn = i18n.language === "he"
  const [isTranslateModelEnabled, setIsTranslateModelEnabled] = useState(() => {
    if (isGlobalTranslationOn) { return false }
    const storedValue = localStorage.getItem("isTranslateModelEnabled");
    return storedValue !== null ? JSON.parse(storedValue) : false}
  );
  const dir = isTranslateModelEnabled ? "rtl" : "ltr"
  const hebrewTranslations = useSelector(getTranslations("he"))
  const allPossibleExpressions = useSelector(getAllPossibleExpressions)

  useEffect(() => {
    localStorage.setItem("isTranslateModelEnabled", JSON.stringify(isTranslateModelEnabled))
    setIsTranslateModelEnabledBS(isTranslateModelEnabled)

    if (isTranslateModelEnabled && allPossibleExpressions.length) {
      
      dispatch(translateExpressions(allPossibleExpressions))
    }
   
  }, [isTranslateModelEnabled]);    
  

  const getLabelByIsTranslateModelEnabled = (expression) => {
    if (isGlobalTranslationOn && isTranslateModelEnabled && hebrewTranslations[expression]) {
      return hebrewTranslations[expression]
    }
    return expression
  } 

  const getColumnHeaderLabel = (columnDef) => {
    const { id, header: currentLabel } = columnDef
    const translation = hebrewTranslations[id]
    if (!isTranslateModelEnabled || !isGlobalTranslationOn) {
      return currentLabel
    }
    if (id === "BIMSearch_grouping_header") {
      let result = ""
      const stack = []
      const expressions = []
      // extract expressions from brackets
      for (let i = 0; i < currentLabel.length; i++) {
        const char = currentLabel[i]
    
        if (char === "[") {
          stack.push(i)
        } else if (char === "]" && stack.length > 0) {
          const startIdx = stack.pop()
          const expression = currentLabel.substring(startIdx + 1, i)
          expressions.push({
            original: expression,
            start: startIdx,
            end: i
          })
        }
      }
      
      // build the result by replacing expressions with their translations
      let lastIndex = 0
      for (let exp of expressions) {
        result += currentLabel.substring(lastIndex, exp.start)
    
        const translated = hebrewTranslations[exp.original]
        result += `[${translated ? translated : exp.original}]`
    
        lastIndex = exp.end + 1
      }

      result += currentLabel.substring(lastIndex)
    
      return result;
    }
    if (!isTranslateModelEnabled || !translation) {
      return currentLabel
    }
    // check for extra chars like " Σ" in "Area Σ"
    if (id.length === currentLabel.length) {
      return translation
    }
    const numOfExtraChars = currentLabel.length - id.length
    return `${translation + currentLabel.slice(-numOfExtraChars)}`
  }

  useEffect(() => {
    const isHomePage = window.location.pathname === "/";
    
    if (isHomePage) {
      localStorage.removeItem("breadcrumbsData");
      return;
    }
    
    if (!foldersTree || !activeProject || !activeFolder) {
      const breadcrumbsData = JSON.parse(
        localStorage.getItem("breadcrumbsData")
      );
      
      if (
        breadcrumbsData?.activeProject ||
        breadcrumbsData?.activeFolder ||
        breadcrumbsData?.foldersTree
      ) {
        setActiveProject(breadcrumbsData?.activeProject);
        setActiveFolder(breadcrumbsData?.activeFolder);
        setFoldersTree(breadcrumbsData?.foldersTree);
      }
    }
  }, []);

  return (
    <DashboardStateContext.Provider
      value={{
        foldersTree,
        activeFolder,
        activeProject,
        isFolderLoading,
        favourites,
        recent,
        isTableHidden,
        isModalLoaded,
        checkSidebarDropdownStatus,
        isWelcomeModalSeen,
        isTranslateModelEnabled,
        dir,
        setIsTranslateModelEnabled,
        signingState, // signinState: 0 - initial state, 1 - after new user form, 2 - after animation
        setFoldersTree,
        setActiveFolder,
        setActiveProject,
        setIsFolderLoading,
        setFavourites,
        setRecent,
        setIsTableHidden,
        setIsModalLoaded,
        setCheckSidebarDropdownStatus,
        setIsWelcomeModalSeen,
        setSigningState,
        getLabelByIsTranslateModelEnabled,
        getColumnHeaderLabel
      }}
    >
      {children}
    </DashboardStateContext.Provider>
  );
};

const useDashboardState = () => useContext(DashboardStateContext);

export { DashboardStateProvider, useDashboardState };
